import { ConvexError } from 'convex/values';

import { BrowserUploadError } from '../browser-multipart-upload-transport';
import type { FolderUploadFailureReason } from './types';

/** A storage PUT the upload proxy or storage answered with an error status. */
export class FolderUploadHttpError extends Error {
  constructor(readonly status: number) {
    super(`FOLDER_UPLOAD_HTTP_${status}`);
    this.name = 'FolderUploadHttpError';
  }
}

/** Backend codes meaning the member may not write to the target folder. */
const PERMISSION_ERROR_CODES = new Set([
  'ACCESS_DENIED',
  'INSUFFICIENT_WORKSPACE_PERMISSION',
  'VFS_HOME_OUTPUT_NOT_AUTHORIZED',
  'VFS_PATH_NOT_ACCESSIBLE',
  'VFS_PERMISSION_DENIED',
  'VFS_PROTECTED_MOUNTPOINT_IMMUTABLE',
  'WORKSPACE_PATH_ORG_MISMATCH',
]);

/** Server refusal codes (beyond the ones handled inline) and their reason. */
const SERVER_REFUSALS: ReadonlyArray<[string, FolderUploadFailureReason]> = [
  // The stored bytes did not verify (the session is quarantined).
  ['BROWSER_UPLOAD_CONTENT_MISMATCH', 'unverified'],
  ['WORKSPACE_UPLOAD_SIZE_INVALID', 'too_large'],
  // Transient: a fresh attempt starts a new session.
  ['BROWSER_UPLOAD_SESSION_EXPIRED', 'network'],
  ['BROWSER_UPLOAD_BEGIN_IN_PROGRESS', 'network'],
];

/** The code of a ConvexError, without any `:detail` suffix such as a path. */
export function convexErrorCode(error: unknown): string | null {
  if (!(error instanceof ConvexError)) return null;
  const data: unknown = error.data;
  const raw =
    typeof data === 'string'
      ? data
      : data !== null && typeof data === 'object'
        ? Reflect.get(data, 'code')
        : null;
  return typeof raw === 'string' ? (raw.split(':')[0] ?? null) : null;
}

function errorText(error: unknown): string {
  if (error instanceof ConvexError && typeof error.data === 'string')
    return error.data;
  return error instanceof Error ? error.message : '';
}

/**
 * The plain reason for a file that failed to upload. Raw error text is never
 * shown; only known cases get specific copy, the rest are `unknown`. A user
 * cancel is not a failure: callers check their own signal first.
 */
export function folderUploadFailureReason(
  error: unknown
): FolderUploadFailureReason {
  const text = errorText(error);
  if (text.includes('WORKSPACE_STORAGE_LIMIT_EXCEEDED')) return 'storage_limit';
  if (text.includes('WORKSPACE_UPLOADS_BUSY')) return 'uploads_busy';
  if (
    text.includes('WORKSPACE_UPLOAD_ACTIVE_CONTENT') ||
    text.includes('WORKSPACE_UPLOAD_UNSUPPORTED_TYPE')
  )
    return 'active_content';
  // Every other refusal the upload route names (its CLIENT_ERRORS, plus the
  // begin size bound) gets its own reason, never the generic one.
  for (const [code, reason] of SERVER_REFUSALS)
    if (text.includes(code)) return reason;
  if (error instanceof BrowserUploadError) {
    if (error.code === 'UPLOAD_CONTENT_UNVERIFIED') return 'unverified';
    if (error.code === 'WORKSPACE_STORAGE_LIMIT_EXCEEDED')
      return 'storage_limit';
    return 'network';
  }
  const code = convexErrorCode(error);
  if (code !== null && PERMISSION_ERROR_CODES.has(code)) return 'permission';
  if (error instanceof FolderUploadHttpError) {
    if (error.status === 401 || error.status === 403) return 'permission';
    if (error.status === 413) return 'storage_limit';
    // Only a storage/proxy PUT raises this (route refusals carry their code
    // in the text, mapped above), so a 415/422 here is not a refusal: it
    // stays retryable. An expired URL (410) retries with a fresh one.
    if (error.status === 410) return 'network';
    return error.status === 429 || error.status >= 500 ? 'network' : 'unknown';
  }
  // A file moved, deleted or locked after it was picked (Safari reports
  // NotReadableError; Chrome NotFoundError).
  if (
    error instanceof DOMException &&
    (error.name === 'NotReadableError' || error.name === 'NotFoundError')
  )
    return 'unreadable';
  // fetch reports a dropped connection as a TypeError; upload-via-proxy turns
  // a stalled transfer into a timeout.
  if (
    error instanceof TypeError ||
    /timed out|network|failed to fetch/i.test(text)
  )
    return 'network';
  return 'unknown';
}

/**
 * Whether a failure fails the same way on Retry, so Retry is not offered:
 * refused content or size, and a server-side permission refusal. A 401/403
 * from the signed storage URL is not final: Retry mints a fresh URL.
 */
export function isFinalFolderUploadFailure(
  error: unknown,
  reason: FolderUploadFailureReason
): boolean {
  if (reason === 'active_content' || reason === 'too_large') return true;
  if (reason !== 'permission') return false;
  const code = convexErrorCode(error);
  return code !== null && PERMISSION_ERROR_CODES.has(code);
}
